import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Check, X, Building2, Mail, Phone, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    awaiting_approval: { label: "Awaiting review", cls: "bg-mr-warning text-white" },
    approved: { label: "Approved", cls: "bg-mr-success text-white" },
    rejected: { label: "Rejected", cls: "bg-mr-accent text-white" },
  };
  const m = map[status] || { label: status, cls: "bg-mr-secondary text-white" };
  return <Badge className={`rounded-full ${m.cls}`}>{m.label}</Badge>;
};

export default function SuperAdminApplications() {
  const [apps, setApps] = useState([]);
  const [status, setStatus] = useState("awaiting_approval");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`/super-admin/clinic-registrations?status=${status}`);
    setApps(data);
  };
  useEffect(() => { load(); }, [status]);

  const approve = async (a) => {
    if (!window.confirm(`Approve "${a.clinic_name}" and create their tenant + admin account?`)) return;
    try {
      await api.post(`/super-admin/clinic-registrations/${a.id}/approve`);
      toast.success("Approved — clinic onboarded");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Approve failed")); }
  };

  const reject = async () => {
    try {
      await api.post(`/super-admin/clinic-registrations/${rejecting.id}/reject`, { reason });
      toast.success("Rejected");
      setRejecting(null); setReason("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reject failed")); }
  };

  return (
    <AdminShell title="Clinic applications" scope="super">
      <div className="flex items-center gap-2 mb-6">
        {[
          { v: "awaiting_approval", l: "Awaiting review" },
          { v: "approved", l: "Approved" },
          { v: "rejected", l: "Rejected" },
          { v: "all", l: "All" },
        ].map((t) => (
          <button
            key={t.v}
            data-testid={`apps-filter-${t.v}`}
            onClick={() => setStatus(t.v)}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${status === t.v ? "bg-mr-primary text-white" : "bg-white border border-mr text-mr-secondary hover:text-mr-primary"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {apps.map((a) => (
          <div key={a.id} data-testid={`app-${a.id}`} className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: a.brand_color || "#0F8964" }}>
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-mr-primary">{a.clinic_name}</h3>
                  <p className="text-xs text-mr-secondary">/{a.slug}</p>
                </div>
              </div>
              <StatusBadge status={a.status} />
            </div>

            <div className="space-y-1.5 text-sm">
              <p className="text-mr-primary"><b>{a.admin_name}</b></p>
              <p className="text-mr-secondary flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {a.work_email}</p>
              {a.phone && <p className="text-mr-secondary flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {a.phone}</p>}
              <p className="text-mr-secondary flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> Applied {new Date(a.created_at).toLocaleString()}</p>
              {a.notes && <p className="text-mr-secondary text-sm border-t border-mr-border pt-2 mt-2 italic">"{a.notes}"</p>}
              {a.status === "rejected" && a.reject_reason && <p className="text-mr-accent text-xs mt-2">Reason: {a.reject_reason}</p>}
            </div>

            {a.status === "awaiting_approval" && (
              <div className="mt-4 border-t border-mr-border pt-4 flex gap-2 justify-end">
                <Button data-testid={`app-reject-${a.id}`} variant="outline" size="sm" className="rounded-full" onClick={() => { setRejecting(a); setReason(""); }}>
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button data-testid={`app-approve-${a.id}`} size="sm" className="rounded-full bg-mr-success hover:opacity-90 text-white" onClick={() => approve(a)}>
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
              </div>
            )}
          </div>
        ))}
        {apps.length === 0 && (
          <div className="lg:col-span-2 bg-white border border-mr rounded-2xl p-12 text-center">
            <Clock className="h-8 w-8 mx-auto text-mr-secondary mb-3" />
            <p className="font-display text-lg font-semibold text-mr-primary">No applications {status === "awaiting_approval" ? "awaiting review" : `marked ${status}`}</p>
            <p className="text-mr-secondary text-sm mt-1">New clinic applications will appear here.</p>
          </div>
        )}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Reject application</DialogTitle></DialogHeader>
          <p className="text-mr-secondary text-sm mb-3">Optionally tell <b>{rejecting?.clinic_name}</b> why their application was not accepted. They'll receive an email.</p>
          <Textarea data-testid="app-reject-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. We need additional verification documents…" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button data-testid="app-reject-confirm" onClick={reject} className="rounded-full bg-mr-accent hover:bg-[var(--mr-accent-hover)]">
              <X className="h-4 w-4 mr-1" /> Reject application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
